// React
import { useEffect, useRef } from "react";

import {
  continuesPendingSequence,
  eventToChord,
  getPendingSequence,
  isTypingTarget,
  normalizeSpec,
  registerShortcut,
  SEQUENCE_TIMEOUT_MS,
  setPendingSequence,
} from "./hotkeys";

export {
  formatHotkey,
  hotkeyToText,
  isMac,
  onOpenShortcutsHelp,
  openShortcutsHelp,
  continuesPendingSequence,
  registerShortcut,
} from "./hotkeys";

// Editing shortcuts that must keep working in fields even when a page binds them
const EDITING_CHORDS = new Set([
  "mod+a",
  "mod+c",
  "mod+v",
  "mod+x",
  "mod+z",
  "mod+y",
  "mod+shift+z",
]);

const toBinding = (value) => (typeof value === "function" ? { handler: value } : value || {});

/**
 * Global keyboard shortcuts, with sequences, per-binding options and the "?" help registry.
 *
 * Rules, unless a binding says otherwise:
 * - plain keys (no ⌘/Ctrl) are ignored while the user types in a field, while a dialog is open
 *   and, for Enter and Space, while a button or link has the focus;
 * - "mod+…" and "escape" also work in fields, except the editing ones (mod+c, mod+v, mod+z…);
 * - events already handled (defaultPrevented) are ignored;
 * - the handler's event gets preventDefault, unless the handler returns false.
 * Handlers may change on every render: the latest ones are used.
 *
 * @param {Record<string, Function | {
 *   handler: (event: KeyboardEvent) => (void|boolean),
 *   description?: string,  // translated: shown in the "?" dialog when set
 *   group?: string,        // translated group title in the dialog (defaults to options.group)
 *   allowInInput?: boolean,
 *   allowInDialog?: boolean,
 *   enabled?: boolean,
 * }>} bindings keys are specs: "?", "n", "mod+k", "shift+enter", "g c" (see hooks/ui/hotkeys.js)
 * @param {{ enabled?: boolean, group?: string }} [options]
 *
 * @example
 * useHotkeys({
 *   "mod+k": { handler: openPalette, description: t("shell:shortcuts.search") },
 *   "g c": { handler: () => navigate("/clients"), description: t("shell:shortcuts.clients"), group: t("ui:shortcuts.groups.navigation") },
 * });
 */
export default function useHotkeys(bindings, { enabled = true, group } = {}) {
  const ref = useRef(bindings);
  ref.current = bindings;

  // Key listener: one per hook instance
  useEffect(() => {
    if (!enabled) return undefined;
    let pending = null; // first chord of a sequence: { chord, at }
    const owner = {}; // identity of this instance in the shared pending sequence

    const onKeyDown = (event) => {
      if (event.defaultPrevented || event.isComposing) return;
      const chord = eventToChord(event);
      if (!chord) return;
      // Second key of a sequence started by another instance: leave it to that one
      const continuesOther = continuesPendingSequence(event, owner);
      if (getPendingSequence()?.owner === owner) setPendingSequence(null);

      // Normalised view of the current bindings
      const table = new Map();
      Object.entries(ref.current || {}).forEach(([spec, value]) => {
        const binding = toBinding(value);
        if (binding.enabled === false || typeof binding.handler !== "function") return;
        table.set(normalizeSpec(spec), binding);
      });

      let spec = chord;
      if (
        pending &&
        Date.now() - pending.at < SEQUENCE_TIMEOUT_MS &&
        table.has(`${pending.chord} ${chord}`)
      ) {
        spec = `${pending.chord} ${chord}`;
      }
      pending = null;
      if (spec === chord && continuesOther) return;
      const binding = table.get(spec);

      if (!binding) {
        // First key of a sequence ("g" of "g c"): wait for the next one
        const nexts = [...table.keys()]
          .filter((key) => key.startsWith(`${chord} `))
          .map((key) => key.slice(chord.length + 1));
        if (nexts.length > 0 && !isTypingTarget(event.target) && !event.repeat) {
          pending = { chord, at: Date.now() };
          setPendingSequence({ at: pending.at, nexts: new Set(nexts), owner });
        }
        return;
      }

      const target = event.target instanceof Element ? event.target : null;
      const withMod = spec.split(" ").some((part) => part.startsWith("mod+"));
      const isEscape = spec === "escape";
      const allowInInput =
        binding.allowInInput ?? ((withMod && !EDITING_CHORDS.has(spec)) || isEscape);
      const allowInDialog = binding.allowInDialog ?? (withMod || isEscape);

      if (!allowInInput && isTypingTarget(target)) return;
      if (!allowInDialog && document.querySelector('[role="dialog"]')) return;
      if (!withMod && (chord === "enter" || chord === "space") && target?.closest("button, a")) {
        return;
      }
      if (binding.handler(event) === false) return;
      event.preventDefault();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);

  // Help registry: only bindings with a description, and only while enabled
  const descriptor = Object.entries(bindings || {})
    .map(([spec, value]) => {
      const binding = toBinding(value);
      if (!binding.description || binding.enabled === false) return null;
      return [normalizeSpec(spec), binding.description, binding.group || group || ""].join(
        "\u0000"
      );
    })
    .filter(Boolean)
    .join("\u0001");

  useEffect(() => {
    if (!enabled || !descriptor) return undefined;
    const unregister = descriptor.split("\u0001").map((row) => {
      const [keys, description, rowGroup] = row.split("\u0000");
      return registerShortcut({ keys, description, group: rowGroup || undefined });
    });
    return () => unregister.forEach((fn) => fn());
  }, [enabled, descriptor]);
}
